// Typed message protocol between sidepanel/options and the service worker.
// Each request has a corresponding response type discriminated by the same `type`.

import type {
  Flashcard,
  Test,
  Chapter,
  Source,
  Summary,
  QaMessage,
  ChatMessage,
  ChatThread,
  HostConfig,
  SearchHit,
  Scenario,
  ScenarioAttempt,
} from './types';

// Aggregated token + cost stats over a time window.
export interface ApiUsageBucket {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  costUsd: number;
  callCount: number;
}

// Aggregates behind the Today/home view. Streak/"today" use the studier's
// LOCAL calendar days (src/shared/study.ts); both backends honor that.
export interface HomeStats {
  testId: string | null;
  testTitle: string | null;
  examDate: number | null;
  dueCount: number;
  totalCards: number;
  /** Cards whose FSRS state is 'review' — "learned". */
  learnedCards: number;
  reviewedToday: number;
  streakDays: number;
  /** Chapters with due cards, highest due first (capped small for chips). */
  chaptersWithDue: { id: string; title: string; dueCount: number }[];
}

// --- Requests (sidepanel/options -> SW) ---

export type Msg =
  | { type: 'PING' }
  | { type: 'VALIDATE_KEY'; key: string }
  | { type: 'GET_SETTINGS' }
  | { type: 'SET_SETTINGS'; partial: Record<string, unknown> }
  | { type: 'GET_HOST_CONFIGS' }
  | { type: 'SET_HOST_CONFIGS'; configs: HostConfig[] }
  | { type: 'RESET_HOST_CONFIGS' }
  | { type: 'TEST_SELECTORS'; tabId: number; config: HostConfig }
  | { type: 'CAPTURE_CHAPTER'; tabId: number }
  | { type: 'STOP_CAPTURE'; tabId: number }
  | {
      type: 'ATTACH_TEXT_CAPTURE';
      title: string;
      sourceUrl: string;
      markdown: string;
      transcriptCues: { start: number; end: number; text: string }[];
      videos: { srcUrl: string; durationSec: number }[];
      // If set, attach as a new chapter source under this existing chapter.
      // If undefined, create a new chapter.
      targetChapterId?: string;
      // Which test a newly-created chapter lands under (the panel's active
      // test). Ignored when targetChapterId is set. Falls back to the SW's
      // default-test resolution when undefined.
      targetTestId?: string;
    }
  | {
      type: 'INGEST_PASTED';
      title: string;
      format: 'chapter' | 'vtt';
      raw: string;
      // Test the newly-created chapter lands under (the panel's active test).
      targetTestId?: string;
    }
  | { type: 'LIST_RECENT_VTTS' }
  | { type: 'CLEAR_RECENT_VTTS' }
  | {
      type: 'ATTACH_VTT_CAPTURE';
      vttId: string;
      chapterTitle: string;
      // If set, attach as a new video source under this existing chapter.
      // If undefined, create a new chapter from the VTT.
      targetChapterId?: string;
      // Test a newly-created chapter lands under (the panel's active test).
      // Ignored when targetChapterId is set.
      targetTestId?: string;
    }
  | { type: 'GENERATE_SUMMARY'; chapterId: string }
  | { type: 'GENERATE_CARDS'; chapterId: string; replace?: boolean }
  | { type: 'LIST_TESTS' }
  | { type: 'CREATE_TEST'; title: string }
  | {
      type: 'UPDATE_TEST';
      testId: string;
      // exam_date: ms epoch; null clears the date.
      patch: { title?: string; order?: number; exam_date?: number | null };
    }
  | { type: 'DELETE_TEST'; testId: string }
  | { type: 'GET_ACTIVE_TEST' }
  | { type: 'SET_ACTIVE_TEST'; testId: string | null }
  // testId scopes the list to one test; omit for all chapters.
  | { type: 'LIST_CHAPTERS'; testId?: string }
  | { type: 'GET_CHAPTER'; chapterId: string }
  | {
      type: 'UPDATE_CHAPTER';
      chapterId: string;
      patch: { title?: string; order?: number; test_id?: string };
    }
  | { type: 'DELETE_CHAPTER'; chapterId: string }
  | { type: 'DELETE_SOURCE'; sourceId: string }
  | { type: 'ASK'; chapterId: string; question: string }
  | { type: 'GET_DUE_CARDS'; chapterIds?: string[] }
  | {
      type: 'RATE_CARD';
      cardId: string;
      rating: 1 | 2 | 3 | 4;
      durationMs: number;
      // Restricts the next card to this filter set (so consecutive rates
      // stay within the same focus). Undefined = all chapters.
      chapterIds?: string[];
    }
  | { type: 'SEEK_VIDEO_IN_TAB'; tabId: number; seconds: number }
  | { type: 'SCROLL_TO_HEADING_IN_TAB'; tabId: number; anchorSlug: string }
  | { type: 'OPEN_ANCHOR'; chapterId: string; anchor: string; kind: 'timestamp' | 'heading' }
  | { type: 'GET_API_USAGE_STATS' }
  | {
      type: 'REGENERATE_ALL';
      summary: boolean;
      cards: boolean;
      replaceCards: boolean;
      // Scope the run to one test. Undefined = every chapter in every test.
      testId?: string;
    }
  | { type: 'POMODORO_START' }
  | { type: 'POMODORO_STOP' }
  | { type: 'POMODORO_STATE' }
  | { type: 'CHAT_GET_SNAPSHOT' }
  // Multi-chat: threads are addressed by chatId. The model picks which chapters
  // to load via tools (pinning was removed).
  | { type: 'LIST_CHATS' }
  | { type: 'CREATE_CHAT'; title?: string }
  | { type: 'RENAME_CHAT'; chatId: string; title: string }
  | { type: 'DELETE_CHAT'; chatId: string }
  // Duplicate a thread (messages + pinned context) into a new chat so a
  // conversation can branch without losing the original.
  | { type: 'FORK_CHAT'; chatId: string }
  | { type: 'CHAT_LIST_MESSAGES'; chatId: string }
  | { type: 'CHAT_ASK'; chatId: string; question: string }
  | { type: 'EXPORT_LIBRARY' }
  | { type: 'IMPORT_LIBRARY'; payloadJson: string; replace: boolean; resetFsrs?: boolean }
  // Full-text search across all chapters within a test. Returns one hit per
  // matching chapter, best snippet first.
  | { type: 'SEARCH_TEST'; testId: string; query: string }
  // Aggregates for the Today/home view, scoped to one test (or all).
  | { type: 'GET_HOME_STATS'; testId?: string }
  // --- Scenario roleplay (scoped to the active test's domain) ---
  // Generate a new scenario. `prompt` is free-form input; `preset` names a
  // SCENARIO_PRESETS entry (one of the two is set). `webSearch` (default true)
  // lets the model search to ground the scenario; false omits the tool.
  // `requestId` is a client-generated id used to filter streaming progress
  // (the scenario id doesn't exist until generation finishes).
  | {
      type: 'GENERATE_SCENARIO';
      testId: string;
      prompt?: string;
      preset?: string;
      webSearch?: boolean;
      requestId: string;
    }
  | { type: 'LIST_SCENARIOS'; testId: string }
  | { type: 'ANSWER_SCENARIO'; scenarioId: string; selectedOption: number }
  | { type: 'DELETE_SCENARIO'; scenarioId: string };

// --- Responses (SW -> caller) ---

export type Res =
  | { type: 'PING'; ok: true }
  | { type: 'VALIDATE_KEY'; ok: boolean; error?: string }
  | { type: 'GET_SETTINGS'; settings: Record<string, unknown> }
  | { type: 'SET_SETTINGS'; ok: true }
  | { type: 'GET_HOST_CONFIGS'; configs: HostConfig[] }
  | { type: 'SET_HOST_CONFIGS'; ok: true }
  | { type: 'RESET_HOST_CONFIGS'; configs: HostConfig[] }
  | { type: 'TEST_SELECTORS'; title: string; preview: string; videoCount: number; error?: string }
  | {
      type: 'CAPTURE_CHAPTER';
      chapterTitle: string;
      breadcrumbs: string[];
      sourceUrl: string;
      pages: { title: string; url: string; markdown: string; breadcrumbs: string[] }[];
      transcriptCues: { start: number; end: number; text: string }[];
      videos: { srcUrl: string; durationSec: number }[];
      warning?: string;
    }
  | { type: 'STOP_CAPTURE'; ok: true }
  | { type: 'ATTACH_TEXT_CAPTURE'; chapterId: string; sourceId: string }
  | { type: 'INGEST_PASTED'; chapterId: string; cardCount: number }
  | {
      type: 'LIST_RECENT_VTTS';
      captures: {
        id: string;
        vttUrl: string;
        tabId: number;
        tabTitle: string;
        pageTitle?: string;
        tabUrl: string;
        capturedAt: number;
      }[];
    }
  | { type: 'CLEAR_RECENT_VTTS'; ok: true }
  | { type: 'ATTACH_VTT_CAPTURE'; chapterId: string; sourceId: string }
  | { type: 'GENERATE_SUMMARY'; summaryId: string }
  | { type: 'GENERATE_CARDS'; cardCount: number }
  | { type: 'LIST_TESTS'; tests: (Test & { chapterCount: number; dueCount: number })[] }
  | { type: 'CREATE_TEST'; test: Test }
  | { type: 'UPDATE_TEST'; ok: true }
  | { type: 'DELETE_TEST'; ok: true }
  | { type: 'GET_ACTIVE_TEST'; activeTestId: string | null }
  | { type: 'SET_ACTIVE_TEST'; ok: true }
  | { type: 'LIST_CHAPTERS'; chapters: (Chapter & { cardCount: number; dueCount: number })[] }
  | { type: 'GET_CHAPTER'; chapter: Chapter; sources: Source[]; summary?: Summary; qa: QaMessage[] }
  | { type: 'UPDATE_CHAPTER'; ok: true }
  | { type: 'DELETE_CHAPTER'; ok: true }
  | { type: 'DELETE_SOURCE'; ok: true }
  | { type: 'ASK'; reply: string }
  | { type: 'GET_DUE_CARDS'; cards: Flashcard[]; total: number }
  | { type: 'RATE_CARD'; ok: true; nextCard?: Flashcard }
  | { type: 'SEEK_VIDEO_IN_TAB'; ok: boolean; framesTried: number; framesWithVideo: number; diag?: string }
  | { type: 'SCROLL_TO_HEADING_IN_TAB'; ok: boolean; headingText?: string }
  | {
      type: 'OPEN_ANCHOR';
      action: 'seeked' | 'scrolled' | 'opened-tab' | 'copied' | 'failed';
      detail?: string;
    }
  | { type: 'REGENERATE_ALL'; ok: true; chapterCount: number }
  | {
      type: 'GET_API_USAGE_STATS';
      total: ApiUsageBucket;
      last7d: ApiUsageBucket;
      today: ApiUsageBucket;
      byTaskType: { taskType: string; bucket: ApiUsageBucket }[];
      byModel: { model: string; bucket: ApiUsageBucket }[];
      callCount: number;
    }
  | { type: 'POMODORO_START'; ok: true }
  | { type: 'POMODORO_STOP'; ok: true }
  | { type: 'POMODORO_STATE'; running: boolean; phase: 'work' | 'break' | 'idle'; remainingSec: number }
  | {
      type: 'CHAT_GET_SNAPSHOT';
      chapters: Chapter[];
      summaries: { chapterId: string; tldr: string; sectionLabels: string[] }[];
      tagsByChapter: Record<string, string[]>;
      sourceCharsByChapter: Record<string, number>;
    }
  | { type: 'CHAT_LIST_MESSAGES'; messages: ChatMessage[] }
  | { type: 'LIST_CHATS'; chats: ChatThread[] }
  | { type: 'CREATE_CHAT'; chat: ChatThread }
  | { type: 'RENAME_CHAT'; ok: true }
  | { type: 'DELETE_CHAT'; ok: true }
  | { type: 'FORK_CHAT'; chat: ChatThread }
  | { type: 'CHAT_ASK'; reply: string }
  | {
      type: 'EXPORT_LIBRARY';
      payloadJson: string;
      stats: { chapters: number; sources: number; summaries: number; flashcards: number };
      schema: string;
      bytes: number;
    }
  | {
      type: 'IMPORT_LIBRARY';
      added: { tests: number; chapters: number; sources: number; summaries: number; flashcards: number };
      cleared: {
        tests: number;
        chapters: number;
        sources: number;
        summaries: number;
        flashcards: number;
        qa_messages: number;
        chat_messages: number;
        review_logs: number;
      };
    }
  | { type: 'SEARCH_TEST'; results: SearchHit[] }
  | { type: 'GET_HOME_STATS'; stats: HomeStats }
  | { type: 'GENERATE_SCENARIO'; scenario: Scenario }
  | { type: 'LIST_SCENARIOS'; scenarios: Scenario[] }
  | { type: 'ANSWER_SCENARIO'; attempt: ScenarioAttempt }
  | { type: 'DELETE_SCENARIO'; ok: true };

// --- Broadcasts (SW -> all listeners; fire-and-forget) ---

export type Broadcast =
  | { type: 'CAPTURE_PROGRESS'; pageNum: number; status: string }
  | { type: 'POMODORO_BREAK_START' }
  | { type: 'POMODORO_BREAK_END' }
  | { type: 'INGEST_DONE'; chapterId: string }
  | { type: 'VTT_BUFFER_UPDATED' }
  | {
      type: 'REGEN_PROGRESS';
      chapterId: string;
      chapterTitle: string;
      step: 'summary' | 'cards' | 'done';
      // 'start' fires before the model call so the UI can render an in-flight
      // row with a spinner. 'end' fires after, with ok/error/cardCount.
      phase: 'start' | 'end';
      // index/total help the UI render an X / N counter; index is 1-based.
      index: number;
      total: number;
      // Only meaningful on phase='end'.
      ok: boolean;
      error?: string;
      // For 'cards' steps that succeeded, how many cards landed.
      cardCount?: number;
      // Source content size (chars) — surfaced on phase='start' so the user
      // can see when a chapter's content is unusually large.
      contentChars?: number;
      // Wall-clock duration of this step in ms — set on phase='end'.
      elapsedMs?: number;
    }
  | { type: 'REGEN_COMPLETE'; total: number; failed: number }
  | {
      // Streaming tick. Throttled to ~100ms in the SW so UIs don't drown.
      // For 'qa' / 'chat', `text` is the cumulative reply so far so the UI
      // can render the typewriter without diff-tracking. For 'regen', `text`
      // is empty and `chars` is the cumulative byte count.
      type: 'LLM_DELTA';
      surface: 'regen' | 'qa' | 'chat' | 'scenario';
      chapterId?: string;
      chatId?: string;
      // Scenario generation filter — the client-provided requestId from
      // GENERATE_SCENARIO (the scenario id doesn't exist until generation ends).
      requestId?: string;
      step?: 'summary' | 'cards';
      // For QA/chat: full assistant text so far. For regen: '' (use chars).
      text: string;
      chars: number;
    };

// --- Content-script protocol (SW <-> CS) ---

export type CsMsg =
  | { type: 'BEGIN_CAPTURE'; config: HostConfig; dryRun?: boolean }
  | { type: 'STOP_CAPTURE' }
  | { type: 'FETCH_VTT'; url: string }
  | { type: 'SEEK_VIDEO'; videoSelector: string; seconds: number }
  | { type: 'SCROLL_TO_HEADING'; anchorSlug: string }
  | { type: 'PROBE_HOST'; config: HostConfig };

export type CsRes =
  | {
      type: 'CAPTURE_DONE';
      chapterTitle: string;
      breadcrumbs: string[];
      pages: { title: string; url: string; markdown: string; breadcrumbs: string[] }[];
      videos: { srcUrl: string; durationSec: number }[];
      transcriptCues: { start: number; end: number; text: string }[];
      error?: string;
    }
  | { type: 'STOP_CAPTURE'; ok: true }
  | { type: 'FETCH_VTT'; ok: boolean; body?: string; error?: string }
  | { type: 'SEEK_VIDEO'; ok: boolean; error?: string }
  | { type: 'SCROLL_TO_HEADING'; ok: boolean; matchedText?: string; error?: string }
  | { type: 'PROBE_HOST'; title: string; preview: string; videoCount: number; transcriptCueCount: number; error?: string };

// Convenience helper for typed sendMessage. In an extension context, dispatches
// over chrome.runtime.sendMessage to the service worker. In a webapp context
// (no chrome.runtime), routes to the HTTP dispatcher in src/web/api.ts so
// route components don't have to know which transport is underneath them.
//
// Throws when the SW returned an error envelope or the API call failed, so
// callers can rely on .catch / try/catch instead of having to check for
// `'error' in r`.
export async function send<T extends Msg['type']>(
  msg: Extract<Msg, { type: T }>
): Promise<Extract<Res, { type: T }>> {
  if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
    const { dispatchMsg } = await import('@/web/api');
    return dispatchMsg(msg);
  }
  const r = await chrome.runtime.sendMessage(msg);
  if (r && typeof r === 'object' && 'error' in r) {
    const err = (r as { error: unknown }).error;
    throw new Error(typeof err === 'string' ? err : JSON.stringify(err));
  }
  return r as Extract<Res, { type: T }>;
}
